import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { VentanasPuePpdBanner } from "@/features/cargar-cfdi/components/VentanasPuePpdBanner";
import { useCargarZip } from "@/features/cargar-zip/hooks/use-cargar-zip";
import type { CargarZipResultado } from "@/features/cargar-zip/types";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { PageHeader, ReadOnlyBanner, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/cargar-zip";
import { CargarZipActions } from "./CargarZipActions";
import { CfdisCargadosCard } from "./CfdisCargadosCard";
import { DiagnosticoCard } from "./DiagnosticoCard";
import { ZipDropzone } from "./ZipDropzone";
import { ZipReuploadWarning } from "./ZipReuploadWarning";

/**
 * Container del paso "Cargar ZIP" (subtipo MULTIPLES_CFDI).
 * Mantiene únicamente el estado del último resultado para mostrar el
 * diagnóstico bajo el dropzone · todo lo demás se delega.
 */
export function CargarZipPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const [resultado, setResultado] = useState<CargarZipResultado | null>(null);

  const upload = useCargarZip(id);

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (P29) · sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const advertirReupload = !soloLectura && sol.cfdis.length > 0 && !upload.isPending;

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title={`Cargar ZIP · Solicitud #${sol.numeroSolicitud}`}
        description="FB60 · MULTIPLES_CFDI · sube un único ZIP con N pares XML+PDF (decisión 51)."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Cargar ZIP" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} paso="cfdi" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="Los CFDIs cargados desde el ZIP no pueden reemplazarse." />}
          {advertirReupload && <ZipReuploadWarning />}

          {/* Ventanas de recepción PUE/PPD vigentes (P41 · D-1 · RN-10: misma
              validación que la carga única, aplicada a cada CFDI del ZIP). */}
          {!soloLectura && <VentanasPuePpdBanner solicitudId={id} />}

          {soloLectura && sol.cfdis.length > 0 ? (
            <CfdisCargadosCard cfdis={sol.cfdis} />
          ) : (
            <ZipDropzone
              cargando={upload.isPending}
              onFile={(zip) => {
                setResultado(null);
                upload.mutate(zip, { onSuccess: setResultado });
              }}
            />
          )}

          {resultado && <DiagnosticoCard resultado={resultado} />}

          <CargarZipActions puedeContinuar={!!resultado?.f6Autorizada && !!resultado?.reglasAutorizadas} onContinuar={() => navigate({ to: "/solicitudes/$id", params: { id } })} />
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
